import { PlusIcon } from '../../assets/svg';
import {
  PayoutConfigResponse,
  PayoutModelType,
  PayoutProviderRule,
  PayoutTier,
} from '../../types/location.types';
// Direct file imports on purpose - the styles barrel triggers a circular init.
import { Div, Icon } from '../../styles/styles';
import { H5 } from '../../styles/text';
import { Separator } from '../../components/navigation/navigation.styled';
import { Button } from '../../components/button/button';
import { AdminSelect } from '../AdminSelect/AdminSelect';
import { MoneyInput, PercentageInput } from './components/PayoutNumberInputs';
import { ProviderRulesSection } from './components/ProviderRulesSection';
import { TierRow } from './components/TierRow';
import { SectionNote } from './components/payoutSections.styled';
import { PAYOUT_MODEL_OPTIONS, createEmptyTier } from './payoutConfig.helpers';
import {
  providerRuleAllowance,
  serviceSupportsProviderRules,
  tiersCoverAllAmounts,
} from './payoutProviderRules';

export type ConfigPatch = Partial<Omit<PayoutConfigResponse, 'service'>>;

/**
 * The stored exceptions of the tier covering the same amount window. Matched
 * on the window rather than on position: tiers can only be edited while the
 * hybrid model is selected, but a saved tier may still have moved in the list
 * since, and an exception restored onto the wrong window would be a silent
 * change of what a provider is paid.
 */
const savedTierRules = (
  saved: PayoutConfigResponse,
  tier: PayoutTier
): PayoutProviderRule[] =>
  saved.tiers.find(
    (candidate) =>
      candidate.minAmount === tier.minAmount &&
      candidate.maxAmount === tier.maxAmount
  )?.providerRules ?? [];

type Props = {
  config: PayoutConfigResponse;
  onChange: (patch: ConfigPatch) => void;
  /**
   * This service as the API has it. Only read when the model changes, to tell
   * a switch back to the saved model from a switch to any other one.
   */
  savedConfig?: PayoutConfigResponse;
  disabled?: boolean;
};

/**
 * The editable body of one service's config. Shared by the inline accordion
 * row and by the standalone modal, so both stay in step.
 */
export const PayoutConfigFields = ({
  config,
  onChange,
  savedConfig,
  disabled,
}: Props) => {
  const usesPercentage =
    config.model === PayoutModelType.bracket ||
    config.model === PayoutModelType.hybrid;
  const usesFlatFee =
    config.model === PayoutModelType.flat_fee ||
    config.model === PayoutModelType.hybrid;
  const usesTiers = config.model === PayoutModelType.hybrid;

  const showProviderRules = serviceSupportsProviderRules(config.service);

  // On a hybrid the config values are what an amount outside every rule falls
  // back to, and its exceptions with them. Once the rules leave no gap they can
  // never fire - hidden then, unless some are stored and need to be seen to go.
  const configRulesReachable =
    !usesTiers || !tiersCoverAllAmounts(config.tiers);

  /**
   * The plain percentage and flat fee survive a model switch (hidden, not
   * erased) so switching away and back doesn't destroy unsaved work -
   * `toPayoutConfigDto` drops whatever the model doesn't use on submit.
   *
   * Exceptions belong to one model: a bracket exception carries a percentage,
   * a flat fee one an amount. Dragging them into another model showed a list
   * of providers from the old rule with an empty value beside them, which
   * reads as a saved rule and is not one - so any other model starts clean.
   * Landing back on the model the API has restores exactly what is stored
   * there, which is what makes trying a model out and changing your mind
   * cost nothing.
   */
  const handleModelChange = (model: PayoutModelType) => {
    const restoring = model === savedConfig?.model;

    onChange({
      model,
      providerRules: restoring ? savedConfig.providerRules : [],
      tiers: config.tiers.map((tier) => ({
        ...tier,
        providerRules: restoring ? savedTierRules(savedConfig, tier) : [],
      })),
    });
  };

  const updateTier = (index: number, patch: Partial<PayoutTier>) =>
    onChange({
      tiers: config.tiers.map((tier, i) =>
        i === index ? { ...tier, ...patch } : tier
      ),
    });

  return (
    <Div $flex $column $gap="12px">
      <AdminSelect
        $textCenter
        label="Payout Model"
        value={config.model}
        disabled={disabled}
        onChange={(e) => handleModelChange(e.target.value as PayoutModelType)}
        options={
          <>
            {PAYOUT_MODEL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </>
        }
      />

      <Div $flex $columnSM $gap="12px">
        {usesPercentage && (
          <PercentageInput
            label="Bracket Percentage"
            value={config.percentage}
            onChange={(percentage) => onChange({ percentage })}
            disabled={disabled}
          />
        )}
        {usesFlatFee && (
          <MoneyInput
            label="vGrubs Flat Fee"
            value={config.flatFee}
            onChange={(flatFee) => onChange({ flatFee })}
            disabled={disabled}
          />
        )}
      </Div>

      {showProviderRules &&
        (configRulesReachable || !!config.providerRules.length) && (
          <ProviderRulesSection
            rules={config.providerRules}
            allow={providerRuleAllowance(config.model)}
            disabled={disabled}
            note={
              !usesTiers || !config.tiers.length
                ? undefined
                : configRulesReachable
                ? 'These are used only for order amounts that no rule below covers. Each rule has its own exceptions.'
                : 'The rules below cover every order amount, so these are never used. Delete them, or change the ranges so some amounts fall outside every rule.'
            }
            noteIsWarning={!configRulesReachable}
            onChange={(providerRules) => onChange({ providerRules })}
          />
        )}

      {usesTiers && (
        <>
          <Separator />
          <Div>
            <H5 $m="0" $mb="4">
              Amount Rules
            </H5>
            <SectionNote>
              Optional. Charge differently depending on how big the order is. An
              amount that falls outside every rule uses the values above. Order
              amount = what the marketplace reports paying (Uber Eats and
              Grubhub), otherwise the order subtotal.
            </SectionNote>
          </Div>

          {config.tiers.map((tier, index) => (
            <TierRow
              key={index}
              tier={tier}
              index={index}
              disabled={disabled}
              showProviderRules={showProviderRules}
              onChange={(patch) => updateTier(index, patch)}
              onRemove={() =>
                onChange({
                  tiers: config.tiers.filter((_, i) => i !== index),
                })
              }
            />
          ))}

          {!disabled && (
            <Div $flex $justifyCenter $mt="4">
              <Button
                $small
                $w="170px"
                $wSM="170px"
                onClick={() =>
                  onChange({
                    tiers: [...config.tiers, createEmptyTier(config.tiers)],
                  })
                }
              >
                <Div $flex $alignCenter $justifyCenter $gap="6px">
                  <Icon $size="14">
                    <PlusIcon />
                  </Icon>
                  Add Rule
                </Div>
              </Button>
            </Div>
          )}
        </>
      )}
    </Div>
  );
};
